import React, { useRef } from 'react';
import { useTranslation } from 'next-i18next';
import { camelCase } from 'lodash';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';

import Divider from 'src/components/ui/Divider';
import Typography from 'src/components/ui/Typography';
import SimpleHorizontalUserCard from 'src/components/ui/SimpleHorizontalUserCard';
import Icon from 'src/components/ui/Icon';
import Loader from 'src/components/ui/Loader';
import { Tab, Tabs } from 'src/components/ui/Tabs';

import type { FollowersQuery, FollowingQuery, Medium, User, UserProfileQuery } from 'src/types/__generated__/graphql';
import { colours } from 'src/constants/colours';
import { isBottomDiv } from 'src/utils/isBottom';
import { styledComponent, styledTag } from 'src/utils/styled';
import { useFollowingQuery, useFollowersQuery } from 'src/hooks/__generated__/queries';
import useUser from 'src/hooks/useUser';
import useAlerts from 'src/hooks/useAlerts';
import type { FollowTab } from 'src/components/profile/follow/FollowModal';
import FollowUnFollowButton from 'src/components/ui/FollowUnFollowButton';
import Avatar from 'src/components/ui/Avatar';
import useAppRouter from 'src/hooks/useAppRouter';
import { routePathCompany } from 'src/constants/router';

const StyledOverflow = styledComponent(Box)`
    overflow: hidden;
`;

const StyledCardActionArea = styledComponent(CardActionArea)`
    ${(props: { $padding?: string; $minHeight?: string; component?: string }) =>
        `
        ${props.$padding ? `padding: ${props.$padding};` : ''}
        ${props.$minHeight ? `min-height: ${props.$minHeight};` : ''}
    `}
    display: flex;
    align-items: center;
    justify-content: flex-start;
`;

const StyledTabs = styledComponent(Tabs)`
    box-sizing: border-box;
    margin-top: 1rem;
    padding: 0 1.5rem;
`;

const RightArea = styledTag('div')`
    margin-left: auto;
`;

type Following = FollowingQuery['following'];
type FollowingResultItem = ArrayElement<Following['results']>;

type Followers = FollowersQuery['followers'];
type FollowersResultItem = ArrayElement<Followers['results']>;

interface Props {
    user: NonNullable<UserProfileQuery['userProfile']>;
    selectedTab: FollowTab;
    setSelectedTab: (tab: FollowTab) => void;
}

const defaultTake = 10;
const FollowModalContent: React.FC<Props> = ({ user, selectedTab, setSelectedTab }) => {
    const { t } = useTranslation(['profile', 'common']);
    const router = useAppRouter();
    const currentUser = useUser();
    const { addAlert } = useAlerts();
    const scrollerContainerRef = useRef<HTMLDivElement | null>(null);

    const {
        data: following,
        fetchMore: fetchMoreFollowing,
        loading: loadingFollowing,
    } = useFollowingQuery({
        variables: {
            userId: user.id,
            limit: defaultTake,
            offset: 0,
        },
        notifyOnNetworkStatusChange: true,
    });

    const {
        data: followers,
        fetchMore: fetchMoreFollowers,
        loading: loadingFollowers,
    } = useFollowersQuery({
        variables: {
            userId: user.id,
            limit: defaultTake,
            offset: 0,
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'cache-and-network',
    });

    const allDataLoaded = {
        following: Boolean(following && following.following.results.length > following.following.total),
        followers: Boolean(followers && followers.followers.results.length > followers.followers.total),
    };

    const handleTabChange = (_ev: React.SyntheticEvent, tab: FollowTab) => {
        if (scrollerContainerRef.current) {
            scrollerContainerRef.current.scrollTop = 0;
        }
        setSelectedTab(tab);
    };

    const handleScroll = async (ev: React.UIEvent<HTMLDivElement, UIEvent>) => {
        const bottom = isBottomDiv(ev.currentTarget);
        if (bottom) {
            if (selectedTab === 'following' && !loadingFollowing && !allDataLoaded.following) {
                try {
                    await fetchMoreFollowing({
                        variables: {
                            offset: following?.following.results.length,
                        },
                    });
                } catch (error: unknown) {
                    addAlert({
                        type: 'error',
                        message: (error as Error).message,
                    });
                }
            }
            if (selectedTab === 'followers' && !loadingFollowers && !allDataLoaded.followers) {
                try {
                    await fetchMoreFollowers({
                        variables: {
                            offset: followers?.followers.results.length,
                        },
                    });
                } catch (error: unknown) {
                    addAlert({
                        type: 'error',
                        message: (error as Error).message,
                    });
                }
            }
        }
    };

    const follows = selectedTab === 'following' ? following?.following.results : followers?.followers.results;
    const isLoading = selectedTab === 'following' ? loadingFollowing : loadingFollowers;

    return (
        <Box display="flex" flexDirection="column" height="100%">
            <StyledTabs value={selectedTab} onChange={handleTabChange} variant="fullWidth">
                <Tab
                    label={t('profile:followingCount', {
                        count: user.numberOfFollows,
                        plurial: user.numberOfFollows > 1 ? 's' : '',
                    })}
                    value="following"
                />
                <Tab
                    label={t('profile:followersCount', {
                        count: user.numberOfFollowers,
                        plurial: user.numberOfFollowers > 1 ? 's' : '',
                    })}
                    value="followers"
                />
            </StyledTabs>
            <Box mx={-3}>
                <Divider />
            </Box>
            <Box flex={1} overflow="auto">
                <Box
                    ref={scrollerContainerRef}
                    display="flex"
                    flexDirection="column"
                    onScroll={handleScroll}
                    paddingX="1.5rem"
                >
                    {!follows?.length && !isLoading && (
                        <Box
                            display="flex"
                            flexDirection="column"
                            height="20rem"
                            justifyContent="center"
                            alignItems="center"
                        >
                            <Icon
                                color={colours.primary.main}
                                size="7.5rem"
                                name={selectedTab === 'following' ? 'FollowingGradient' : 'FollowersGradient'}
                            />
                            <Box mt={2}>
                                <Typography variant="body2" align="center" data-target-id="follow-modal-content-empty">
                                    {t(
                                        selectedTab === 'following'
                                            ? 'profile:followingEmptyState'
                                            : 'profile:followersEmptyState',
                                        { fullName: user.fullName },
                                    )}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    {follows?.map((follow) => {
                        const toDisplay =
                            selectedTab === 'following'
                                ? (follow as FollowingResultItem).followedOwner.user ??
                                  (follow as FollowingResultItem).followedOwner.company
                                : (follow as FollowersResultItem).followingUser;

                        if (!toDisplay) {
                            return null;
                        }

                        if (toDisplay.__typename === 'User') {
                            return (
                                <SimpleHorizontalUserCard
                                    dataTargetId={`simple-horizontal-card-${toDisplay.id}`}
                                    user={toDisplay as User}
                                    firstSubtitle={
                                        toDisplay.isProfessionnal
                                            ? null // send null to not display activity in this case
                                            : undefined
                                    }
                                    secondSubtitle={
                                        toDisplay.id === currentUser?.id
                                            ? undefined // send undefined to not display common relations for current user
                                            : 'commonRelationsCount'
                                    }
                                    minHeight="5rem"
                                    padding="0.5rem 1rem"
                                    backgroundColor="transparent"
                                    rightArea={
                                        <FollowUnFollowButton user={toDisplay} refetchQueries={['UserProfile']} />
                                    }
                                />
                            );
                        }

                        if (toDisplay.__typename === 'Company') {
                            return (
                                <Card key={toDisplay.id} elevation={0} sx={{ backgroundColor: 'transparent' }}>
                                    <StyledCardActionArea
                                        component="div"
                                        $padding="0.5rem 1rem"
                                        $minHeight="5rem"
                                        onClick={() => router.push(`${routePathCompany}/${toDisplay.id}`)}
                                    >
                                        <Avatar
                                            image={toDisplay.image as Medium}
                                            alt={toDisplay.name}
                                            size={40}
                                            isCompany
                                        />
                                        <StyledOverflow pl={2}>
                                            <Typography variant="h5">{toDisplay.name}</Typography>
                                            <Box display="flex">
                                                <Typography variant="subtitle2" noWrap>
                                                    {t(
                                                        `common:companyActivities.${camelCase(
                                                            toDisplay.activities?.[0].labelKey,
                                                        )}`,
                                                    )}
                                                </Typography>
                                            </Box>
                                        </StyledOverflow>
                                        <RightArea>
                                            <FollowUnFollowButton
                                                company={toDisplay}
                                                refetchQueries={['UserProfile']}
                                            />
                                        </RightArea>
                                    </StyledCardActionArea>
                                </Card>
                            );
                        }
                    })}
                    {isLoading && <Loader />}
                </Box>
            </Box>
        </Box>
    );
};

export default FollowModalContent;
